import { ReactNode } from "react";
import { FieldValues, UseFormHandleSubmit } from "react-hook-form";
import currency from "currency.js";
import moment from "moment";
import { ReactComponent as Alert } from "../../assets/svg/alert.svg";
import { GetOrders200Response, SideResponse } from "../../codegen-api";
import { COLORS } from "../../constants/design/colors";
import { SPACING } from "../../constants/design/spacing";
import { IPositionInfo, ITradeInfo } from "../../interfaces/TradeInfo";
import { nanosToSeconds } from "../../utils/date";
import { getStopOrderName } from "../../utils/order";
import { startCase } from "../../utils/strings";
import {
  Form,
  InfoRow,
  PlaceOrderButton,
  PositionChip,
  PositionInfoWrapper,
  Title,
  TradeInfoWrapper,
  Value,
} from "../TradeForm/style";
import { Spinner } from "../shared/Spinner";
import { ModalErrorMessage } from "../shared/style";
import { EditOrderInfoTag } from "./style";
import { AccountStateEnum } from "../../contexts/AuthContext";
import { Button, ButtonThemeEnum } from "../Buttons/styles";

interface IRenderFormContentParams {
  order?: GetOrders200Response;
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  onEditOrder: () => Promise<void>;
  t: (key: any, options?: any) => any;
  translateAll: (key: any, options?: any) => any;
  priceSizeInputs: ReactNode;
  tradeInfo: ITradeInfo[];
  positionInfo: IPositionInfo[];
  accountSigningKeyState: AccountStateEnum;
  loading: boolean;
  hasAnyError: boolean;
  errorMessage?: string;
  setShowConnectModal: (value: boolean) => void;
}

export function renderFormContent({
  order,
  handleSubmit,
  onEditOrder,
  t,
  translateAll,
  priceSizeInputs,
  tradeInfo,
  positionInfo,
  accountSigningKeyState,
  loading,
  hasAnyError,
  errorMessage,
  setShowConnectModal,
}: IRenderFormContentParams) {
  if (!order) {
    return undefined;
  }

  return (
    <Form style={{ padding: 0 }} onSubmit={handleSubmit(onEditOrder)}>
      <EditOrderInfoTag
        $color={
          order.side === SideResponse.Buy ? COLORS.up.one : COLORS.down.one
        }
        background={
          order.side === SideResponse.Buy ? COLORS.up.five : COLORS.down.five
        }
      >
        <div>{t(order.side)}</div>
        {order.option_type && order.expiry && order.strike ? (
          <>
            <div>{startCase(t(order.option_type.toLowerCase()))}</div>
            <div>
              {moment.unix(nanosToSeconds(order.expiry)).format("DD MMM YY")}
            </div>
            <div>{currency(order.strike).format()}</div>
          </>
        ) : (
          <div>
            {order.stop
              ? //@ts-ignore
                getStopOrderName(translateAll, order.order_type, order.stop)
              : t(order.order_type.toLowerCase())}
          </div>
        )}
      </EditOrderInfoTag>
      {priceSizeInputs}
      <TradeInfoWrapper>
        {tradeInfo.map((info) => (
          <InfoRow
            key={`${info.title}-${info.value}}`}
            warningOrError={info.warningOrError}
          >
            <Title>
              {info.title}
              {Boolean(info.warningOrError && info.showErrorIcon) && (
                <Alert
                  style={{
                    stroke:
                      info.warningOrError === "warning"
                        ? COLORS.system.one
                        : COLORS.negative.one,
                    marginLeft: `${SPACING.one}px`,
                    marginTop: `-${SPACING.one / 2}px`,
                  }}
                />
              )}
            </Title>
            <Value>{info.value}</Value>
          </InfoRow>
        ))}
      </TradeInfoWrapper>

      <PositionInfoWrapper>
        {positionInfo.map((info) => (
          <InfoRow
            key={`${info.title}-${info.value}}`}
            warningOrError={info.warningOrError}
          >
            <Title>
              {info.title}
              {Boolean(info.warningOrError && info.showErrorIcon) && (
                <Alert
                  style={{
                    stroke:
                      info.warningOrError === "warning"
                        ? COLORS.system.one
                        : COLORS.negative.one,
                    marginLeft: `${SPACING.one}px`,
                    marginTop: `-${SPACING.one / 2}px`,
                  }}
                />
              )}
              {info.side && (
                <PositionChip $type={info.side}>
                  {info.side === SideResponse.Buy ? t("long") : t("short")}
                </PositionChip>
              )}
            </Title>
            <Value>{info.value}</Value>
          </InfoRow>
        ))}
      </PositionInfoWrapper>

      {accountSigningKeyState !== AccountStateEnum.OK ? (
        <Button
          buttonTheme={ButtonThemeEnum.HIGHLIGHT}
          fullWidth
          style={{ marginBottom: 0 }}
          onClick={() => setShowConnectModal(true)}
        >
          {t("complete_sign_in")}
        </Button>
      ) : (
        <PlaceOrderButton
          disabled={loading || hasAnyError}
          type={"submit"}
          side={order.side}
          style={{ marginBottom: 0 }}
        >
          {
            // eslint-disable-next-line no-nested-ternary
            !loading ? (
              t("update_order")
            ) : (
              <Spinner
                color={
                  order.side === SideResponse.Buy
                    ? COLORS.up.one
                    : COLORS.down.one
                }
              />
            )
          }
        </PlaceOrderButton>
      )}

      {errorMessage && <ModalErrorMessage>{errorMessage}</ModalErrorMessage>}
    </Form>
  );
}
